package com.dualremotetest

import com.dualremotetest.NesPalette as P

/**
 * Draws the game into a 256×224 ARGB framebuffer ([px]) – the Famicom picture with its usual 8 overscan lines cut
 * from the top and bottom – plus a [SIDE]-pixel panel on each side for scores and timers during play (our addition;
 * the TV's widescreen has room beside the 8:7 picture). [screen] holds all three side by side. The host scales it up
 * by a whole number with nearest-neighbour sampling, so every game pixel stays square and sharp. Plain Kotlin: the
 * same code renders on the TV and in JVM tests (preview sheets).
 */
class PixelRenderer {
    val px = IntArray(W * H)
    private val left = IntArray(SIDE * H)
    private val right = IntArray(SIDE * H)
    /** What the TV shows: the 1P panel, the picture and the 2P panel ([FULL_W] × [H]). */
    val screen = IntArray(FULL_W * H)
    /** Where the primitives draw: [px], or a side panel while [into] runs. */
    private var buf = px
    private var bw = W
    private var cx0 = 0
    private var cy0 = 0
    private var cx1 = W
    private var cy1 = H

    fun render(m: TankMatch) {
        clip(0, 0, W, H)
        when (m.phase) {
            Phase.TITLE -> title(m)
            Phase.SETTINGS -> settings(m)
            Phase.JOIN -> join(m)
            Phase.STAGE_INTRO -> intro(m)
            Phase.PLAYING -> { field(m); hud(m) }
            Phase.PAUSED -> { field(m); hud(m); pauseMenu(m) }
            Phase.TALLY -> tally(m)
            Phase.GAME_OVER -> { fill(0, 0, W, H, P.BLACK); bigText("GAME", 56); bigText("OVER", 104) }
            Phase.HIGH_SCORE -> highScore(m)
            Phase.VICTORY -> victory(m)
            Phase.LOBBY -> lobby(m)
        }
        m.banner?.let { banner(it) }
        m.netStatus?.let { banner(it) }
        panels(m)
        for (y in 0 until H) {
            left.copyInto(screen, y * FULL_W, y * SIDE, y * SIDE + SIDE)
            px.copyInto(screen, y * FULL_W + SIDE, y * W, y * W + W)
            right.copyInto(screen, y * FULL_W + SIDE + W, y * SIDE, y * SIDE + SIDE)
        }
    }

    private inline fun into(target: IntArray, width: Int, draw: () -> Unit) {
        buf = target
        bw = width
        clip(0, 0, width, H)
        draw()
        buf = px
        bw = W
        clip(0, 0, W, H)
    }

    /**
     * Side panels, during play only: 1P on the left, 2P on the right (score, stars, helmet seconds). Shared effects
     * (clock, shovel) at the bottom left; high score and a non-NORMAL difficulty at the bottom right.
     */
    private fun panels(m: TankMatch) {
        val play = m.phase == Phase.PLAYING || m.phase == Phase.PAUSED
        into(left, SIDE) {
            fill(0, 0, SIDE, H, P.BLACK)
            if (play) {
                playerPanel(m, m.players[0], "1P", PANEL_P1)
                var y = 168
                for ((type, frames) in listOf(PowerUpType.CLOCK to m.clockFramesLeft(), PowerUpType.SHOVEL to m.shovelFramesLeft())) {
                    if (frames <= 0) continue
                    blit(PixelArt.ITEMS.getValue(type), 8, y, P.ITEM)
                    text(seconds(frames), 30, y + 4, P.WHITE)
                    y += 20
                }
            }
        }
        into(right, SIDE) {
            fill(0, 0, SIDE, H, P.BLACK)
            if (play) {
                if (m.twoPlayer) playerPanel(m, m.players[1], "2P", PANEL_P2)
                text("HI", 8, 176, PANEL_DIM)
                text(minOf(m.hiScore, 999_999).toString().padStart(6, '0'), 8, 186, P.WHITE)
                if (m.runDifficulty != Difficulty.NORMAL) text(m.runDifficulty.label, 8, 200, P.ORANGE)
            }
        }
    }

    private fun playerPanel(m: TankMatch, p: PlayerTank, label: String, color: Int) {
        text(label, 8, 16, color)
        text("SCORE", 8, 32, PANEL_DIM)
        text(minOf(p.score, 999_999).toString().padStart(6, '0'), 8, 42, P.WHITE)
        for (i in 0 until 3) blit(PixelArt.STAR_ICON, 8 + i * 12, 58, if (i < p.stars) STAR_ON else STAR_OFF)
        if (p.state == PlayerState.OUT && p.reviveFrames > 0) {
            // Co-op revival countdown, in this player's panel.
            text("BACK IN", 4, 76, PANEL_DIM)
            text2x(seconds(p.reviveFrames), 12, 88, color)
            return
        }
        val helmet = m.helmetFramesLeft(p)
        if (helmet > 0) {
            blit(PixelArt.ITEMS.getValue(PowerUpType.HELMET), 8, 74, P.ITEM)
            text(seconds(helmet), 30, 78, P.WHITE)
        }
    }

    private fun seconds(frames: Int) = minOf(99, (frames + 59) / 60).toString().padStart(2, ' ')

    /** Renders one stage's starting terrain (for the numbered preview sheet). */
    fun renderStagePreview(t: Terrain) {
        clip(0, 0, W, H)
        fill(0, 0, W, H, P.GREY)
        fill(FX, FY, FIELD, FIELD, P.BLACK)
        clip(FX, FY, FX + FIELD, FY + FIELD)
        terrainLayer(t, 0, forest = false)
        blit(PixelArt.EAGLE, FX + Spots.HQ_X, FY + Spots.HQ_Y, P.HQ)
        terrainLayer(t, 0, forest = true)
        clip(0, 0, W, H)
    }

    // ================================================================== play field

    private fun field(m: TankMatch) {
        fill(0, 0, W, H, P.GREY)
        fill(FX, FY, FIELD, FIELD, P.BLACK)
        clip(FX, FY, FX + FIELD, FY + FIELD)
        terrainLayer(m.terrain, m.stageFrame, forest = false)
        blit(if (m.hqAlive) PixelArt.EAGLE else PixelArt.EAGLE_DEAD, FX + Spots.HQ_X, FY + Spots.HQ_Y, P.HQ)
        for (p in m.players) {
            if (!m.isParticipant(p)) continue
            when (p.state) {
                PlayerState.SPAWNING -> spark(p.timer, p.x, p.y)
                PlayerState.ACTIVE -> {
                    val s = PixelArt.player[p.stars.coerceIn(0, 3)][(p.tread shr 1) and 1][p.dir.ordinal]
                    blit(s, FX + p.x, FY + p.y, if (p.index == 0) P.P1 else P.P2)
                    if (p.shielded) blit(PixelArt.SHIELD[((m.uiFrame / 2) % 2).toInt()], FX + p.x, FY + p.y, P.SPARK)
                }
                else -> Unit
            }
        }
        for (e in m.enemies) {
            if (e.state == EnemyState.SPAWNING) {
                spark(e.timer, e.x, e.y)
                continue
            }
            val s = PixelArt.enemy[e.type.ordinal][(e.tread shr 1) and 1][e.dir.ordinal]
            blit(s, FX + e.x, FY + e.y, enemyPalette(e, m.uiFrame))
        }
        for (b in m.bullets) if (b.alive) blit(PixelArt.BULLET, FX + b.x - 1, FY + b.y - 1, P.HIT_WHITE)
        terrainLayer(m.terrain, m.stageFrame, forest = true)
        for (pu in m.powerUps) {
            // ROM: items blink 8 frames on / 8 off on the global frame counter (also while paused).
            if ((m.uiFrame shr 3) and 1L == 1L) blit(PixelArt.ITEMS.getValue(pu.type), FX + pu.x, FY + pu.y, P.ITEM)
        }
        for (x in m.explosions) {
            val s = boomSprite(x) ?: continue
            blit(s, FX + x.x - s.w / 2, FY + x.y - s.h / 2, P.EXPLOSION)
        }
        for (pop in m.popups) if (pop.delay == 0) {
            val t = pop.points.toString()
            mini(t, FX + pop.x - (t.length * 4 - 1) / 2, FY + pop.y - 2, P.WHITE)
        }
        if (m.ending == Ending.DEFEATED && m.endTimer < TankMatch.GAME_OVER_FRAMES) {
            // ROM: "GAME OVER" rises 1 px per frame from the bottom of the screen for 127 frames, then stays.
            val risen = minOf(TankMatch.GAME_OVER_RISE, TankMatch.GAME_OVER_FRAMES - m.endTimer)
            val y = 224 - risen
            text("GAME", FX + FIELD / 2 - 16, FY + y, P.RED)
            text("OVER", FX + FIELD / 2 - 16, FY + y + 9, P.RED)
        }
        if (m.phase == Phase.PLAYING && m.stageFrame < CURTAIN_FRAMES) curtain(CURTAIN_FRAMES - m.stageFrame)
        clip(0, 0, W, H)
    }

    /** Grey curtain bands from the top and bottom; [step] 0 = open, 16 = closed. */
    private fun curtain(step: Int) {
        clip(0, 0, W, H)
        val h = H / 2 * step / CURTAIN_FRAMES
        fill(0, 0, W, h, P.GREY)
        fill(0, H - h, W, h, P.GREY)
    }

    private fun boomSprite(x: Explosion): Sprite? {
        val small = PixelArt.SMALL_BOOM
        val big = PixelArt.BIG_BOOM
        return when (x.kind) {
            Boom.BULLET -> small[(x.age / 3).coerceIn(0, 2)]
            Boom.TANK, Boom.FAST_TANK, Boom.PLAYER -> {
                val phase = (x.age * 6 / x.length).coerceIn(0, 5)
                listOf(small[0], small[1], small[2], big[0], big[1], if (x.kind == Boom.PLAYER) small[0] else small[2])[phase]
            }
            Boom.HQ -> {
                val seq = listOf(small[0], small[1], small[2], big[0], big[1], big[0], small[2], small[1], small[0], null)
                seq[if (x.age < 3) 0 else ((x.age - 3) / 4 + 1).coerceIn(1, 9)]
            }
        }
    }

    private fun terrainLayer(t: Terrain, frame: Int, forest: Boolean) {
        val water = if ((frame / 32) % 2 == 0) P.WATER_A else P.WATER_B
        for (cy in 0 until CELLS) for (cx in 0 until CELLS) {
            val type = t[cx, cy]
            if (forest != (type == T.FOREST)) continue
            val x = FX + cx * CELL
            val y = FY + cy * CELL
            val qx = (cx and 1) * 4
            val qy = (cy and 1) * 4
            when (type) {
                T.BRICK -> part(PixelArt.BRICK, qx, qy, x, y, P.BRICK)
                T.STEEL -> part(PixelArt.STEEL, qx, qy, x, y, P.STEEL)
                T.WATER -> part(PixelArt.WATER, qx, qy, x, y, water)
                T.ICE -> part(PixelArt.ICE, qx, qy, x, y, P.ICE)
                T.FOREST -> part(PixelArt.FOREST, qx, qy, x, y, P.FOREST)
            }
        }
    }

    /**
     * ROM colours: item carriers are silver / red for 8 frames each on the global frame counter (all in step, armor
     * colours hidden); armor alternates every frame by hits left: 4 green/silver, 3 yellow/silver, 2 yellow/green, 1 silver.
     */
    private fun enemyPalette(e: EnemyTank, frame: Long): IntArray {
        val odd = frame and 1L == 1L
        return when {
            e.carrier -> if ((frame shr 3) and 1L == 1L) P.FLASH_RED else P.ENEMY
            e.type == EnemyType.ARMOR -> when (e.hp) {
                4 -> if (odd) P.ENEMY else P.P2
                3 -> if (odd) P.ENEMY else P.P1
                2 -> if (odd) P.P2 else P.P1
                else -> P.ENEMY
            }
            else -> P.ENEMY
        }
    }

    private fun spark(timer: Int, x: Int, y: Int) {
        val i = (timer / 4) % 6
        blit(PixelArt.SPARK[SPARK_ORDER[i]], FX + x, FY + y, P.SPARK)
    }

    // ================================================================== HUD (right-hand panel)

    /** The original's column: enemies still to destroy, lives per player, stage flag. Scores and timers are in [panels]. */
    private fun hud(m: TankMatch) {
        // One icon per enemy still to destroy, on the field or still to come (the original counts only those to come,
        // so its last icon could vanish while enemies were still on the field).
        for (i in 0 until m.enemiesLeft) blit(PixelArt.ENEMY_ICON, 232 + (i % 2) * 8, 16 + (i / 2) * 8, P.HUD)
        playerHud(m.players[0], "1P", 132, P.P1[2])
        if (m.twoPlayer) playerHud(m.players[1], "2P", 158, P.P2[2])
        blit(PixelArt.FLAG, 232, 184, P.FLAG)
        text(m.stage.toString().padStart(2, ' '), 232, 201, P.BLACK)
    }

    /** "1P"/"2P" (the original writes IP/IIP) over the tank icon and the spare count, with a gap between them. */
    private fun playerHud(p: PlayerTank, label: String, y: Int, colour: Int) {
        text(label, 232, y, P.BLACK)
        blit(PixelArt.LIFE_ICON, 230, y + 9, intArrayOf(0, colour, colour, colour))
        text(minOf(p.spares, 9).toString(), 242, y + 9, P.BLACK)
    }

    // ================================================================== screens

    private fun title(m: TankMatch) {
        fill(0, 0, W, H, P.BLACK)
        if (m.pressStart) {
            pressStart(m)
            return
        }
        val o = m.titleScroll
        text("1P", 16, 12 + o, P.WHITE)
        text(m.players[0].score.toString().padStart(6, ' '), 32, 12 + o, P.WHITE)
        text("HI-", 120, 12 + o, P.WHITE)
        text(m.hiScore.toString().padStart(6, ' '), 144, 12 + o, P.WHITE)
        bigText("TANKS", 26 + o)
        bigText("BATTLE", 60 + o)
        bigText("RETRO", 94 + o)
        menu(m, 128 + o, 88)
        controlHints(m, 196 + o, 12)
    }

    /** Before the title menu in a browser (see [TankMatch.pressStart]): the logo and a blinking PRESS START. */
    private fun pressStart(m: TankMatch) {
        text("HI-", 120, 12, P.WHITE)
        text(m.hiScore.toString().padStart(6, ' '), 144, 12, P.WHITE)
        bigText("TANKS", 26)
        bigText("BATTLE", 60)
        bigText("RETRO", 94)
        if ((m.uiFrame / 30) % 2 == 0L) text("PRESS START", centerX("PRESS START"), 146, P.WHITE)
        val how = if (m.keyboardHints) "CLICK OR PRESS ANY KEY" else "TAP THE SCREEN"
        text(how, centerX(how), 170, P.c(0x10))
    }

    private fun settings(m: TankMatch) {
        fill(0, 0, W, H, P.BLACK)
        text("SETTINGS", centerX("SETTINGS"), 16, P.ORANGE)
        menu(m, 32, 40)
        val blurb = m.difficulty.blurb
        if (blurb.isNotEmpty()) text(blurb, centerX(blurb), 142, P.ORANGE)
        text("LEFT/RIGHT: CHANGE", 40, 156, P.c(0x10))
        text("FIRE: SELECT  BACK: TITLE", 24, 168, P.c(0x10))
        controlHints(m, 192, 12)
    }

    private fun menu(m: TankMatch, top: Int, left: Int) {
        val items = m.menuItems()
        val spacing = if (m.phase == Phase.TITLE) 13 else 12
        items.forEachIndexed { i, item ->
            val y = top + i * spacing
            val sel = i == m.menuCursor
            text(m.itemLabel(item), left, y, if (sel) P.WHITE else P.c(0x10))
            if (sel) {
                if (m.phase == Phase.TITLE) {
                    val s = PixelArt.player[0][((m.uiFrame / 4) % 2).toInt()][Dir.RIGHT.ordinal]
                    blit(s, left - 24, y - 4, P.P1)
                } else if ((m.uiFrame / 16) % 2 == 0L) text(">", left - 12, y, P.ORANGE)
            }
        }
    }

    private fun join(m: TankMatch) {
        fill(0, 0, W, H, P.BLACK)
        text("2 PLAYERS", centerX("2 PLAYERS"), 24, P.ORANGE)
        text("PLAYER 1 READY", centerX("PLAYER 1 READY"), 56, P.c(0x2A))
        val blink = (m.uiFrame / 20) % 2 == 0L
        text("PLAYER 2: PRESS FIRE", centerX("PLAYER 2: PRESS FIRE"), 88, if (blink) P.WHITE else P.c(0x10))
        val (where, alt) = if (m.touchDevice) "ON A CONTROLLER" to "OR KEYBOARD" else "ON THE OTHER REMOTE," to "A CONTROLLER OR KEYBOARD"
        text(where, centerX(where), 100, P.WHITE)
        text(alt, centerX(alt), 112, P.WHITE)
        text("BACK: CANCEL", centerX("BACK: CANCEL"), 152, P.c(0x10))
        controlHints(m, 192, 12)
    }

    /**
     * The two hint lines under menus: remotes and keyboards on a TV, touch and controllers on phones and tablets, keys
     * and the mouse in a computer's browser.
     */
    private fun controlHints(m: TankMatch, y: Int, gap: Int) {
        val (first, second) = when {
            m.keyboardHints -> "MOVE: ARROWS, ESDF, IJKL" to "FIRE: SPACE  BACK: ESC"
            m.touchDevice -> "TOUCH: D-PAD + FIRE BUTTON" to "CONTROLLER: FIRE: A"
            else -> "KEYBOARD: FIRE: ENTER / SPACE" to "REMOTE: FIRE: OK"
        }
        text(first, 12, y, P.c(0x10))
        text(second, 12, y + gap, P.c(0x10))
    }

    /** Online lobby: a heading, a room code in large plain letters (the letter being typed underlined), lines, choices. */
    private fun lobby(m: TankMatch) {
        fill(0, 0, W, H, P.BLACK)
        val v = m.lobby?.view ?: return
        text(v.heading, centerX(v.heading), 20, P.ORANGE)
        var y = 44
        v.code?.let { code ->
            solidText(code, CODE_Y, P.WHITE)
            if (v.codeCursor >= 0 && (m.uiFrame / 16) % 2 == 0L) {
                fill((W - code.length * 32) / 2 + v.codeCursor * 32 + 2, 74, 26, 3, P.ORANGE)
            }
            y = 88
        }
        for (line in v.lines) {
            text(line, centerX(line), y, P.WHITE)
            y += 12
        }
        y += 10
        v.items.forEachIndexed { i, item ->
            val sel = i == v.cursor
            text(item, 88, y, if (sel) P.WHITE else P.c(0x10))
            if (sel) blit(PixelArt.player[0][((m.uiFrame / 4) % 2).toInt()][Dir.RIGHT.ordinal], 64, y - 4, P.P1)
            y += 14
        }
        if (v.footer.isNotEmpty()) text(v.footer, centerX(v.footer), 176, P.c(0x10))
        controlHints(m, 196, 12)
    }

    private fun intro(m: TankMatch) {
        if (m.screenTimer < CURTAIN_FRAMES) {
            // ROM: the curtain closes over 16 frames before "STAGE n" shows.
            fill(0, 0, W, H, P.BLACK)
            curtain(m.screenTimer)
            return
        }
        fill(0, 0, W, H, P.GREY)
        val s = "STAGE " + m.stage.toString().padStart(2, ' ')
        text(s, centerX(s), 104, P.BLACK)
        // The original look on NORMAL; otherwise a reminder of the run's difficulty (our addition).
        val d = m.runDifficulty
        if (d != Difficulty.NORMAL) text(d.label, centerX(d.label), 120, P.BLACK)
        if (m.lockstep) {
            val who = "ONLINE: YOU ARE PLAYER ${m.onlinePlayer + 1}"
            text(who, centerX(who), 136, P.BLACK)
        }
        if (m.introSelect) {
            text("UP/DOWN: CHOOSE STAGE", centerX("UP/DOWN: CHOOSE STAGE"), 152, P.BLACK)
            text("FIRE: START", centerX("FIRE: START"), 164, P.BLACK)
        }
    }

    private fun pauseMenu(m: TankMatch) {
        fill(centerX("PAUSE") - 4, 28, 48, 16, P.BLACK)
        if ((m.uiFrame / 16) % 2 == 0L) text("PAUSE", centerX("PAUSE"), 32, P.RED)
        val items = m.menuItems()
        val top = 52
        val bottom = top + 12 + items.size * 11 + 30
        fill(8, top - 6, W - 16, bottom - top + 6, P.BLACK)
        frame(8, top - 6, W - 16, bottom - top + 6, P.WHITE)
        items.forEachIndexed { i, item ->
            val y = top + 4 + i * 11
            val sel = i == m.menuCursor
            text(m.itemLabel(item), 32, y, if (sel) P.WHITE else P.c(0x10))
            if (sel) text(">", 18, y, P.ORANGE)
        }
        controlHints(m, bottom - 22, 11)
    }

    private fun tally(m: TankMatch) {
        val t = m.tally ?: return
        fill(0, 0, W, H, P.BLACK)
        text("HI-SCORE", 56, 16, P.RED)
        text(m.hiScore.toString().padStart(7, ' '), 136, 16, P.ORANGE)
        val st = "STAGE " + t.stage.toString().padStart(2, ' ')
        text(st, centerX(st), 32, P.WHITE)
        text("1-PLAYER", 16, 48, P.RED)
        text(m.players[0].score.toString().padStart(7, ' '), 16, 60, P.ORANGE)
        if (t.twoPlayer) {
            text("2-PLAYER", 176, 48, P.RED)
            text(m.players[1].score.toString().padStart(7, ' '), 176, 60, P.ORANGE)
        }
        for (row in 0 until 4) {
            val y = 80 + row * 24
            if (row > t.row) break
            val pts = EnemyType.entries[row].points
            blit(PixelArt.enemy[row][0][Dir.UP.ordinal], 120, y - 4, P.ENEMY)
            tallyRow(t.shown[0][row], pts, y, left = true)
            if (t.twoPlayer) tallyRow(t.shown[1][row], pts, y, left = false)
        }
        if (t.totalShown) {
            fill(96, 176, 64, 2, P.WHITE)
            text("TOTAL", 40, 184, P.WHITE)
            text(t.total(0).toString().padStart(2, ' '), 88, 184, P.WHITE)
            if (t.twoPlayer) text(t.total(1).toString().padStart(2, ' '), 152, 184, P.WHITE)
            if (t.bonusPlayer >= 0) {
                val x = if (t.bonusPlayer == 0) 8 else 136
                text("BONUS!", x + 16, 198, P.RED)
                text("1000 PTS", x + 8, 208, P.WHITE)
            }
        }
    }

    private fun tallyRow(count: Int, pts: Int, y: Int, left: Boolean) {
        val score = (count * pts).toString().padStart(4, ' ')
        val n = count.toString().padStart(2, ' ')
        if (left) {
            text(score, 8, y, P.WHITE)
            text("PTS", 48, y, P.WHITE)
            text(n, 80, y, P.WHITE)
            text("<", 104, y, P.WHITE)
        } else {
            text(">", 144, y, P.WHITE)
            text(n, 160, y, P.WHITE)
            text(score, 184, y, P.WHITE)
            text("PTS", 224, y, P.WHITE)
        }
    }

    private fun highScore(m: TankMatch) {
        fill(0, 0, W, H, P.BLACK)
        bigText("HISCORE", 48)
        val colors = intArrayOf(P.RED, P.ORANGE, P.WHITE, P.c(0x2A))
        val c = colors[((m.uiFrame / 6) % 4).toInt()]
        val s = m.hiScore.toString()
        text2x(s, (W - s.length * 16) / 2, 112, c)
    }

    private fun victory(m: TankMatch) {
        fill(0, 0, W, H, P.BLACK)
        text("CONGRATULATIONS!", centerX("CONGRATULATIONS!"), 40, P.ORANGE)
        text("ALL 35 STAGES CLEARED", centerX("ALL 35 STAGES CLEARED"), 64, P.WHITE)
        bigText("WIN", 88)
        text("1-PLAYER  " + m.players[0].score.toString().padStart(7, ' '), 32, 140, P.WHITE)
        if (m.twoPlayer) text("2-PLAYER  " + m.players[1].score.toString().padStart(7, ' '), 32, 154, P.WHITE)
        text("HI-SCORE  " + m.hiScore.toString().padStart(7, ' '), 32, 176, P.RED)
    }

    private fun banner(s: String) {
        val w = s.length * 8 + 16
        val x = (W - w) / 2
        fill(x, 98, w, 20, P.BLACK)
        frame(x, 98, w, 20, P.WHITE)
        text(s, x + 8, 104, P.WHITE)
    }

    // ================================================================== primitives

    private fun clip(x0: Int, y0: Int, x1: Int, y1: Int) {
        cx0 = x0; cy0 = y0; cx1 = x1; cy1 = y1
    }

    fun fill(x: Int, y: Int, w: Int, h: Int, c: Int) {
        val xa = maxOf(x, cx0)
        val xb = minOf(x + w, cx1)
        for (yy in maxOf(y, cy0) until minOf(y + h, cy1)) {
            val row = yy * bw
            for (xx in xa until xb) buf[row + xx] = c
        }
    }

    private fun frame(x: Int, y: Int, w: Int, h: Int, c: Int) {
        fill(x, y, w, 1, c)
        fill(x, y + h - 1, w, 1, c)
        fill(x, y, 1, h, c)
        fill(x + w - 1, y, 1, h, c)
    }

    private fun blit(s: Sprite, x: Int, y: Int, pal: IntArray) {
        for (sy in 0 until s.h) {
            val yy = y + sy
            if (yy < cy0 || yy >= cy1) continue
            for (sx in 0 until s.w) {
                val xx = x + sx
                if (xx < cx0 || xx >= cx1) continue
                val v = s[sx, sy]
                if (v != 0) buf[yy * bw + xx] = pal[v]
            }
        }
    }

    /** A 4×4 quarter of an 8×8 tile. */
    private fun part(s: Sprite, qx: Int, qy: Int, x: Int, y: Int, pal: IntArray) {
        for (sy in 0 until 4) {
            val yy = y + sy
            if (yy < cy0 || yy >= cy1) continue
            for (sx in 0 until 4) {
                val xx = x + sx
                if (xx < cx0 || xx >= cx1) continue
                val v = s[qx + sx, qy + sy]
                if (v != 0) buf[yy * bw + xx] = pal[v]
            }
        }
    }

    fun text(s: String, x: Int, y: Int, c: Int) {
        var cx = x
        for (ch in s) {
            PixelArt.FONT[ch.uppercaseChar()]?.let { g ->
                for (gy in 0 until 8) for (gx in 0 until 8) if (g[gy][gx] == '#') put(cx + gx, y + gy, c)
            }
            cx += 8
        }
    }

    private fun text2x(s: String, x: Int, y: Int, c: Int) {
        var cx = x
        for (ch in s) {
            PixelArt.FONT[ch]?.let { g ->
                for (gy in 0 until 8) for (gx in 0 until 8) if (g[gy][gx] == '#') fill(cx + gx * 2, y + gy * 2, 2, 2, c)
            }
            cx += 16
        }
    }

    /** Title-style lettering: each font pixel becomes a 4×4 piece of brick, so words look built from brick walls. */
    private fun bigText(s: String, y: Int) {
        val x0 = (W - s.length * 32) / 2
        s.forEachIndexed { i, ch ->
            val g = PixelArt.FONT[ch] ?: return@forEachIndexed
            for (gy in 0 until 8) for (gx in 0 until 8) {
                if (g[gy][gx] != '#') continue
                part(PixelArt.BRICK, (gx and 1) * 4, (gy and 1) * 4, x0 + i * 32 + gx * 4, y + gy * 4, P.BRICK)
            }
        }
    }

    /** Large plain lettering for the room code: each font pixel becomes a solid 4×4 block, easy to read out. */
    private fun solidText(s: String, y: Int, c: Int) {
        val x0 = (W - s.length * 32) / 2
        s.forEachIndexed { i, ch ->
            val g = PixelArt.FONT[ch] ?: return@forEachIndexed
            for (gy in 0 until 8) for (gx in 0 until 8) if (g[gy][gx] == '#') fill(x0 + i * 32 + gx * 4, y + gy * 4, 4, 4, c)
        }
    }

    private fun mini(s: String, x: Int, y: Int, c: Int) {
        var cx = x
        for (ch in s) {
            if (ch in '0'..'9') {
                val g = PixelArt.MINI_DIGITS[ch - '0']
                for (gy in 0 until 5) for (gx in 0 until 3) if (g[gy][gx] == '#') put(cx + gx, y + gy, c)
            }
            cx += 4
        }
    }

    private fun put(x: Int, y: Int, c: Int) {
        if (x >= cx0 && x < cx1 && y >= cy0 && y < cy1) buf[y * bw + x] = c
    }

    private fun centerX(s: String) = (W - s.length * 8) / 2

    companion object {
        const val W = 256
        const val H = 224
        /** Width of each side panel; [FULL_W] is the whole image the TV shows. */
        const val SIDE = 64
        const val FULL_W = W + 2 * SIDE
        private val PANEL_P1 = P.c(0x28)
        private val PANEL_P2 = P.c(0x2A)
        private val PANEL_DIM = P.c(0x10)
        private val STAR_ON = intArrayOf(0, P.c(0x28), 0, 0)
        private val STAR_OFF = intArrayOf(0, P.c(0x00), 0, 0)
        /** Top-left of the 208×208 field inside the frame. */
        const val FX = 16
        const val FY = 8
        private val SPARK_ORDER = intArrayOf(0, 1, 2, 3, 2, 1)
        const val CURTAIN_FRAMES = 16

        /**
         * The menu row drawn at ([x], [y]) in the picture (0..[W], 0..[H]; the side panels excluded), or null when that
         * point is not on a row or no menu is showing. Follows the layouts of title, settings and pauseMenu.
         */
        fun menuRowAt(m: TankMatch, x: Int, y: Int): Int? {
            val (top, spacing, left) = when (m.phase) {
                Phase.TITLE -> Triple(130 + m.titleScroll, 16, 88 - 24)
                Phase.SETTINGS -> Triple(32, 12, 40 - 16)
                Phase.PAUSED -> Triple(52 + 4, 11, 16)
                else -> return null
            }
            if (x < left || x >= W - 8) return null
            val i = floorDiv(y - top + 2, spacing)
            return if (y - top + 2 >= 0 && i in m.menuItems().indices) i else null
        }

        /** Top of the lobby's large room code. */
        const val CODE_Y = 40

        /**
         * The room code under ([x], [y]) in the picture (side panels excluded) when the lobby shows a copyable one, with
         * a few pixels of slack around the letters so a fingertip finds it; null otherwise.
         */
        fun lobbyCodeAt(m: TankMatch, x: Int, y: Int): String? {
            if (m.phase != Phase.LOBBY) return null
            val v = m.lobby?.view ?: return null
            val code = v.code ?: return null
            if (!v.copyable) return null
            val x0 = (W - code.length * 32) / 2
            val slack = 8
            val inside = x >= x0 - slack && x < x0 + code.length * 32 + slack && y >= CODE_Y - slack && y < CODE_Y + 32 + slack
            return if (inside) code else null
        }

        private fun floorDiv(a: Int, b: Int) = if (a >= 0) a / b else -((-a + b - 1) / b)
    }
}
